import { useEffect, useMemo, useReducer } from "react";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import type {
  LicensesStep,
  PaymentSource,
  PricingPeriodUnit,
} from "common/types";

interface LicensesState {
  currentStep: LicensesStep;
  selectedPeriodUnit: PricingPeriodUnit;
  licenseCount: number;
  accountsToCancel: string[];
  paymentSource: PaymentSource;
}

type LicensesAction =
  | { type: "NEXT_STEP"; payload: LicensesStep }
  | { type: "SET_LICENSE_COUNT"; payload: number }
  | { type: "SET_PERIOD_UNIT"; payload: PricingPeriodUnit }
  | { type: "SET_ACCOUNTS_TO_CANCEL"; payload: string[] }
  | { type: "SET_PAYMENT_SOURCE"; payload: PaymentSource }
  | { type: "RESET"; payload: LicensesState };

function licensesReducer(
  state: LicensesState,
  action: LicensesAction,
): LicensesState {
  const { type, payload } = action;

  switch (type) {
    case "NEXT_STEP":
      return { ...state, currentStep: payload };
    case "SET_LICENSE_COUNT":
      return { ...state, accountsToCancel: [], licenseCount: payload };
    case "SET_PERIOD_UNIT":
      return { ...state, selectedPeriodUnit: payload };
    case "SET_ACCOUNTS_TO_CANCEL":
      return { ...state, accountsToCancel: payload };
    case "SET_PAYMENT_SOURCE":
      return { ...state, paymentSource: payload };
    case "RESET":
      return payload;
    default:
      return state;
  }
}

export default function useLicensesState(newSeatsCount: number = 0) {
  const {
    id: workspaceId,
    license_count: workspaceLicenseCount,
    lifetime_license_count: lifetimeLicenseCount,
  } = useSelectedWorkspaceContext();
  const { workspace } = useWorkspaceDetails(workspaceId);

  const isLifetimeDeal = lifetimeLicenseCount > 0;
  const subscription = workspace?.subscription;
  const licenses = workspace ? workspace.licenses : [];

  const computedLicenseCount = useMemo(() => {
    if (!subscription && !isLifetimeDeal) return 1;

    return newSeatsCount
      ? workspaceLicenseCount + newSeatsCount
      : workspaceLicenseCount;
  }, [workspaceLicenseCount, newSeatsCount, subscription, licenses]);

  const computedPeriodUnit: PricingPeriodUnit = useMemo(
    () => (subscription ? subscription.item_price.period_unit : "month"),
    [subscription],
  );

  const computedStep: LicensesStep = useMemo(
    () => (newSeatsCount ? "seatPurchase" : "manageSeats"),
    [subscription, newSeatsCount, licenses],
  );

  const initialState: LicensesState = {
    currentStep: computedStep,
    selectedPeriodUnit: computedPeriodUnit,
    licenseCount: computedLicenseCount,
    accountsToCancel: [],
    paymentSource: null,
  };

  const [state, dispatch] = useReducer(licensesReducer, initialState);

  // Keep reducer state in sync when newSeatsCount (or related deps) changes
  useEffect(() => {
    dispatch({
      type: "SET_LICENSE_COUNT",
      payload: computedLicenseCount,
    });

    if (state.currentStep === "manageSeats") {
      dispatch({ type: "NEXT_STEP", payload: computedStep });
    }
  }, [computedLicenseCount, computedStep]);

  const assignedLicenseCount = useMemo(
    () => licenses.filter(({ account_id }) => account_id).length,
    [licenses],
  );

  const shouldRemoveAccounts = state.licenseCount < assignedLicenseCount;

  function resetState() {
    dispatch({ type: "RESET", payload: initialState });
  }

  return {
    state,
    dispatch,
    resetState,
    assignedLicenseCount,
    shouldRemoveAccounts,
  };
}
